import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface ArticleVerificationResult {
	verified: boolean;
	verificationCode: string;
	articleIdPrefix: string | null;
	title: string | null;
	description: string | null;
	body: string | null;
	contentHash: string | null;
	publishedAt: string | null;
	primaryCategory: string | null;
	imageUrl: string | null;
	message: string;
}

type PageState = 'idle' | 'loading' | 'verified' | 'not_found' | 'error';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.justknow.co.in';

/** Parse a UTC timestamp string safely — appends Z if missing so JS treats it as UTC, not local. */
function parseUTC(iso: string): Date {
	return new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
}

/** Format ISO-8601 timestamp into a human-readable string in the user's local timezone. */
function formatPublishedAt(iso: string | null): string {
	if (!iso) return 'Unknown';
	try {
		return parseUTC(iso).toLocaleString(undefined, {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	} catch {
		return iso;
	}
}

/** Truncate a long string for display. */
function truncate(text: string | null, maxLen: number): string {
	if (!text) return '—';
	return text.length > maxLen ? text.substring(0, maxLen) + '…' : text;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ArticleVerificationPage
 *
 * Public page that lets anyone verify whether a Justknow article is
 * genuine.  Accessible at /verify/:code without authentication.
 *
 * Calls GET /api/public/verify/{code} on the backend and renders:
 *   – ✅  A green "VERIFIED" card with article details, or
 *   – ❌  A red "NOT FOUND / FABRICATED" warning card.
 */
const ArticleVerificationPage: React.FC = () => {
	const { code } = useParams<{ code: string }>();

	const [state, setState] = useState<PageState>('idle');
	const [result, setResult] = useState<ArticleVerificationResult | null>(null);
	const [errorMessage, setErrorMessage] = useState<string>('');

	// ── Run verification on mount ─────────────────────────────────────────────
	useEffect(() => {
		if (!code) return;

		setState('loading');
		setResult(null);
		setErrorMessage('');

		axios
			.get<ArticleVerificationResult>(
				`${API_BASE}/api/public/verify/${encodeURIComponent(code)}`,
				{ timeout: 10_000, withCredentials: false },
			)
			.then((res) => {
				setResult(res.data);
				setState(res.data.verified ? 'verified' : 'not_found');
			})
			.catch((err) => {
				console.error('Verification API error:', err);
				setErrorMessage(
					err.response?.status === 400
						? 'Invalid verification code format.'
						: 'Unable to reach the verification server. Please try again later.',
				);
				setState('error');
			});
	}, [code]);

	// ─────────────────────────────────────────────────────────────────────────
	// Render
	// ─────────────────────────────────────────────────────────────────────────

	return (
		<div style={styles.pageWrapper}>
			{/* ── Branded header ── */}
			<div style={styles.brandHeader}>
				<span style={styles.brandLogo}>📰</span>
				<span style={styles.brandName}>Justknow</span>
				<span style={styles.brandTagline}>Content Verification Portal</span>
			</div>

			<div style={styles.card}>
				{/* ── Loading ── */}
				{state === 'loading' && (
					<div style={styles.centerContent}>
						<div style={styles.spinner} />
						<p style={styles.loadingText}>Verifying article authenticity…</p>
					</div>
				)}

				{/* ── Idle (no code) ── */}
				{state === 'idle' && (
					<div style={styles.centerContent}>
						<p style={styles.infoText}>
							Enter a verification code in the URL to check an article's authenticity.
						</p>
						<p style={styles.hintText}>
							Example: <code>/verify/JK-550E8400</code>
						</p>
					</div>
				)}

				{/* ── Network / server error ── */}
				{state === 'error' && (
					<div style={styles.centerContent}>
						<div style={styles.statusBadge} data-status='error'>
							⚠️ &nbsp;Verification Unavailable
						</div>
						<p style={{ ...styles.infoText, marginTop: 12 }}>{errorMessage}</p>
					</div>
				)}

				{/* ── VERIFIED ─────────────────────────────────────────────────── */}
				{state === 'verified' && result && (
					<>
						<div style={styles.statusBadge} data-status='verified'>
							✅ &nbsp;Article Verified — This content is genuine
						</div>

						<p style={styles.officialNote}>
							This article was published by Justknow and has not been altered.
							The content below is the official, original version.
						</p>

						{/* Details table */}
						<table style={styles.detailTable}>
							<tbody>
								<tr>
									<td style={styles.labelCell}>Verification Code</td>
									<td style={styles.valueCell}>
										<code style={styles.codeChip}>{result.verificationCode}</code>
									</td>
								</tr>
								{result.articleIdPrefix && (
									<tr>
										<td style={styles.labelCell}>Article Reference</td>
										<td style={styles.valueCell}>{result.articleIdPrefix}</td>
									</tr>
								)}
								{result.primaryCategory && (
									<tr>
										<td style={styles.labelCell}>Category</td>
										<td style={styles.valueCell}>{result.primaryCategory}</td>
									</tr>
								)}
								<tr>
									<td style={styles.labelCell}>Published</td>
									<td style={styles.valueCell}>{formatPublishedAt(result.publishedAt)}</td>
								</tr>
							</tbody>
						</table>

						{result.title && (
							<>
								<div style={styles.sectionLabel}>Official Headline</div>
								<div style={styles.articleTitle}>{result.title}</div>
							</>
						)}

						{result.imageUrl && (
							<img
								src={result.imageUrl}
								alt={result.title || 'Article image'}
								style={{ width: '100%', maxHeight: '360px', objectFit: 'cover', borderRadius: '10px', margin: '16px 0' }}
								onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
							/>
						)}

						{result.body && (
							<>
								<div style={styles.sectionLabel}>Article Content</div>
								<div style={styles.articleDescription}>
									{truncate(result.body, 800)}
								</div>
							</>
						)}
					</>
				)}

				{/* ── NOT FOUND / FABRICATED ────────────────────────────────────── */}
				{state === 'not_found' && result && (
					<>
						<div style={styles.statusBadge} data-status='not_found'>
							❌ &nbsp;Not Found — Content could not be verified
						</div>

						<p style={styles.warningNote}>
							No published Justknow article matches the verification code{' '}
							<strong>{result.verificationCode}</strong>.
						</p>
						<p style={styles.warningNote}>
							This content may have been <strong>fabricated</strong>, altered, or the
							verification code may be incorrect. Do not share or act on unverified
							content.
						</p>
						<p style={styles.reportNote}>
							If you believe genuine Justknow content is being misrepresented,
							please contact us at{' '}
							<a href='mailto:verify@justknow.co.in' style={styles.link}>
								verify@justknow.co.in
							</a>
							.
						</p>
					</>
				)}
			</div>

			{/* ── Footer ── */}
			<div style={styles.footer}>
				<span>
					© {new Date().getFullYear()} Justknow — All rights reserved. &nbsp;|&nbsp;
					<a href='https://justknow.co.in' style={styles.link}>
						justknow.co.in
					</a>
				</span>
			</div>
		</div>
	);
};

// ─────────────────────────────────────────────────────────────────────────────
// Styles (inline — no external CSS dependency)
// ─────────────────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
	pageWrapper: {
		minHeight: '100vh',
		background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f5e9 100%)',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: '32px 16px 48px',
		fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
	},
	brandHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		marginBottom: 28,
	},
	brandLogo: {
		fontSize: 28,
	},
	brandName: {
		fontSize: 22,
		fontWeight: 700,
		color: '#1a237e',
	},
	brandTagline: {
		fontSize: 13,
		color: '#607d8b',
		marginLeft: 4,
		paddingLeft: 10,
		borderLeft: '1px solid #b0bec5',
	},
	card: {
		background: '#ffffff',
		borderRadius: 12,
		boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
		padding: '32px 36px',
		width: '100%',
		maxWidth: 680,
		minHeight: 240,
	},
	centerContent: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		padding: '32px 0',
	},
	spinner: {
		width: 40,
		height: 40,
		border: '4px solid #e3f2fd',
		borderTop: '4px solid #1976d2',
		borderRadius: '50%',
		animation: 'spin 0.8s linear infinite',
		marginBottom: 16,
	},
	loadingText: {
		color: '#607d8b',
		fontSize: 15,
	},
	infoText: {
		color: '#607d8b',
		fontSize: 15,
		textAlign: 'center',
	},
	hintText: {
		color: '#9e9e9e',
		fontSize: 13,
		marginTop: 8,
	},
	statusBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		padding: '10px 18px',
		borderRadius: 8,
		fontSize: 15,
		fontWeight: 600,
		marginBottom: 18,
		// Color applied via state-specific overrides below at render time
		background: '#e8f5e9',
		color: '#2e7d32',
		border: '1px solid #a5d6a7',
	},
	officialNote: {
		fontSize: 14,
		color: '#455a64',
		marginBottom: 20,
		lineHeight: 1.6,
	},
	warningNote: {
		fontSize: 14,
		color: '#b71c1c',
		marginBottom: 14,
		lineHeight: 1.6,
	},
	reportNote: {
		fontSize: 13,
		color: '#607d8b',
		marginTop: 12,
		lineHeight: 1.6,
	},
	detailTable: {
		width: '100%',
		borderCollapse: 'collapse',
		marginBottom: 24,
		fontSize: 14,
	},
	labelCell: {
		padding: '8px 12px 8px 0',
		color: '#607d8b',
		fontWeight: 500,
		width: 160,
		verticalAlign: 'top',
	},
	valueCell: {
		padding: '8px 0',
		color: '#212121',
		verticalAlign: 'top',
	},
	codeChip: {
		background: '#e3f2fd',
		color: '#1565c0',
		padding: '2px 8px',
		borderRadius: 4,
		fontSize: 14,
		letterSpacing: '0.05em',
	},
	sectionLabel: {
		fontSize: 11,
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: '0.08em',
		color: '#90a4ae',
		marginBottom: 6,
		marginTop: 16,
	},
	articleTitle: {
		fontSize: 20,
		fontWeight: 700,
		color: '#1a237e',
		lineHeight: 1.4,
		marginBottom: 4,
	},
	articleDescription: {
		fontSize: 14,
		color: '#455a64',
		lineHeight: 1.7,
	},
	hashBlock: {
		background: '#f5f5f5',
		borderRadius: 6,
		padding: '10px 14px',
		overflow: 'hidden',
		wordBreak: 'break-all',
	},
	hashText: {
		fontSize: 12,
		color: '#37474f',
		fontFamily: "'Courier New', monospace",
	},
	hashNote: {
		fontSize: 12,
		color: '#9e9e9e',
		marginTop: 6,
		lineHeight: 1.5,
	},
	footer: {
		marginTop: 28,
		fontSize: 12,
		color: '#90a4ae',
		textAlign: 'center',
	},
	link: {
		color: '#1565c0',
		textDecoration: 'none',
	},
};

export default ArticleVerificationPage;
